import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";

const links = [
    { to: "/field", label: "Today", tid: "nav-field" },
    { to: "/sales", label: "Sales", tid: "nav-sales" },
    { to: "/due-soon", label: "Due Soon", tid: "nav-due-soon" },
    { to: "/calendar", label: "Calendar", tid: "nav-calendar" },
    { to: "/customers", label: "Customers", tid: "nav-customers" },
    { to: "/reminders", label: "Reminders", tid: "nav-reminders" },
    { to: "/emails", label: "Emails", tid: "nav-emails" },
    { to: "/invoices", label: "Invoices", tid: "nav-invoices" },
    { to: "/expenses", label: "Expenses", tid: "nav-expenses" },
    { to: "/settings", label: "Settings", tid: "nav-settings" },
    { to: "/import", label: "Import", tid: "nav-import" },
];

const Layout = () => {
    const { email, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const doLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[color:var(--dh-paper)]">
            <header className="border-b-4 border-[color:var(--dh-line)] bg-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            className="h-14 w-14 sm:h-16 sm:w-16 flex items-center justify-center border-2 border-[color:var(--dh-line)] bg-[color:var(--dh-ink)] p-1"
                            data-testid="brand-mark"
                        >
                            <img
                                src="/dragon-heat-logo.png"
                                alt="Dragon Heat Plumbing & Heating"
                                className="max-h-full max-w-full object-contain"
                            />
                        </div>
                        <div className="min-w-0 hidden sm:block">
                            <p className="text-label" style={{ color: "var(--dh-muted)" }}>Dragon Heat</p>
                            <h1 className="font-display font-black text-xl leading-none uppercase tracking-tight">
                                Admin
                            </h1>
                        </div>
                    </div>

                    <nav className="hidden md:flex items-center gap-1 ml-8">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.tid}
                                className={({ isActive }) =>
                                    `font-display uppercase font-bold tracking-wide px-4 py-2 border-b-4 ${
                                        isActive
                                            ? "border-[color:var(--dh-orange)] text-black"
                                            : "border-transparent text-neutral-600 hover:text-black"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3 ml-auto">
                        <span className="hidden sm:inline text-xs text-neutral-500 font-mono">{email}</span>
                        <button
                            data-testid="logout-btn"
                            onClick={doLogout}
                            className="dh-btn"
                            data-variant="ghost"
                            aria-label="Sign out"
                        >
                            <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
                        </button>
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="md:hidden dh-btn"
                            data-variant="ghost"
                            aria-label="Menu"
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </div>
                {open && (
                    <div className="md:hidden border-t-2 border-[color:var(--dh-line)] bg-white">
                        <div className="flex flex-col">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`${l.tid}-mobile`}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `font-display uppercase font-bold tracking-wide px-6 py-4 border-b border-neutral-200 ${
                                            isActive ? "bg-[color:var(--dh-orange)] text-white" : "text-black"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                )}
            </header>
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
